import { apiFetch } from '@/shared/api/client';

/**
 * A factura issued through ARCA (electronic invoicing). Only rows with a CAE
 * reach here — manually-uploaded facturas are excluded server-side.
 */
export type FacturaArcaRow = {
  /** Receipt sharepoint_id — pass as GenerarNotaCreditoDialog.facturaReceiptId and FileLink.entityId. */
  id: string;
  nroComprobante: string | null;
  /** "Factura A" / "Factura B" / … (or "-"). */
  tipo: string;
  cae: string | null;
  caeVencimiento: string | null;
  qrUrl: string | null;
  fecha: string | null;
  cliente: string | null;
  clienteId: string | null;
  ventaSpId: string | null;
  nroCotizacion: string | null;
  vendedor: string | null;
  total: number;
  iva: number;
  neto: number;
  /** Σ committed NCs against this factura. */
  ncTotal: number;
  /** Remaining creditable amount = total − ncTotal (clamped ≥ 0). */
  saldo: number;
  /** The receipt (cobro) was anulled — the ARCA comprobante is still real. */
  anulada: boolean;
  /** La VENTA se anuló (el comprobante puede seguir en 'Confirmado'). */
  ventaAnulada: boolean;
  facturaFileName: string | null;
  facturaDriveItemId: string | null;
  /** Emisión ante ARCA, 'YYYY-MM-DD'. De acá corre el plazo (fin de ese mes). */
  fechaEmision: string;
  /** Factura anticipada (Facturación Previa). */
  facturacionPrevia: boolean;
  /** Status del cobro al que pertenece la factura. */
  cobroStatus: string | null;
  /**
   * Huérfanas (cobro/venta anulada): quién aprobó la anulación y cuándo
   * (cualquiera puede ser null en aprobaciones viejas). null = sin aprobación.
   */
  anulacionAprobada: { por: string | null; fecha: string | null } | null;
  /** Sólo en /anticipadas-pendientes: hay una "Solicitar anulación" pendiente. */
  anulacionSolicitada?: boolean;
};

/**
 * CAE huérfano: la factura existe en ARCA pero su cobro o su venta se anuló y
 * nadie emitió la Nota de Crédito que la revierta (`saldo > 0`). Anular es un
 * soft-delete de una sola columna, así que el comprobante fiscal sigue vivo y
 * la única salida es la NC.
 */
export function esCaeHuerfano(r: FacturaArcaRow): boolean {
  return (r.anulada || r.ventaAnulada) && r.saldo > 0;
}

export async function listFacturasArca(): Promise<FacturaArcaRow[]> {
  const res = await apiFetch<{ items: FacturaArcaRow[] }>('/api/facturacion/facturas');
  return res.items;
}

/**
 * Facturas anticipadas todavía sin cobrar (aviso rojo de Home). El server ya
 * filtra por rol: admin/administración todas, ventas/marketing las de sus ventas.
 */
export async function listFacturasAnticipadasPendientes(): Promise<FacturaArcaRow[]> {
  const res = await apiFetch<{ items: FacturaArcaRow[] }>('/api/facturacion/anticipadas-pendientes');
  return res.items;
}
